import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { logger } from './utils/logger';
import { NotificationProvider } from './contexts/NotificationContext';
import { CompanyProvider } from './contexts/CompanyContext';
import ErrorBoundary from './components/Common/ErrorBoundary';
import ContactPage from './pages/ContactPage';
import AuthContainer from './components/Auth/AuthContainer';
import Dashboard from './pages/Dashboard';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import MembershipCards from './pages/PassManager/MembershipCards';
import MembershipManagement from './pages/PassManager/MembershipManagement';
import CouponOffers from './pages/PassManager/CouponOffers';
import Coupons from './pages/PassManager/Coupons';
import UserManagement from './pages/UserManagement';
import CompanyManagement from './pages/CompanyManagement';
import Locations from './pages/Locations';
import ResourceManagement from './pages/ResourceManagement';
import { SecuritySettings } from './pages/SecuritySettings';
import AccountSettings from './pages/AccountSettings';
import AuditLogs from './pages/AuditLogs';
import PerformanceMonitoringDashboard from './pages/PerformanceMonitoringDashboard';
import GDPRManagement from './pages/Settings/GDPRManagement';
import SupportPage from './pages/SupportPage';
import AdminAPIKeysManagement from './pages/AdminAPIKeysManagement';

// Event Tickets pages
import Venues from './pages/EventTickets/Venues';
import Events from './pages/EventTickets/Events';
import Tickets from './pages/EventTickets/Tickets';

// Admin pages
import CreateCompany from './pages/Admin/CreateCompany';
import CompanyList from './pages/Admin/CompanyList';
import EditCompany from './pages/Admin/EditCompany';

import DashboardLayout from './components/Common/DashboardLayout';
import { CircularProgress, Box } from '@mui/material';
import TwoFactorModal from './components/Auth/TwoFactorModal';
import PasswordChangeWrapper from './components/Auth/PasswordChangeWrapper';
import LegalAcceptanceModal from './components/Auth/LegalAcceptanceModal';
import OAuthCallback from './pages/OAuthCallback';

// Import custom fonts
import './assets/fonts/fonts.css';
import './assets/animations.css';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#14264a', // Nova brend boja - tamno plava
      light: '#1e3a6f',
      dark: '#0a1525',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#2563eb', // Komplementarna plava
      light: '#60a5fa',
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    background: {
      default: '#f8fafc', // Light gray background with subtle pattern
      paper: '#ffffff', // Solid white cards
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
  },
  spacing: (factor: number) => `${0.25 * factor}rem`,
  shape: {
    borderRadius: 12, // Modern border radius (12px)
  },
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.05)',
    '0 4px 6px rgba(0,0,0,0.05)',
    '0 10px 25px rgba(0,0,0,0.08)',
    '0 20px 40px rgba(0,0,0,0.08)',
    '0 25px 50px rgba(0,0,0,0.1)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 2px 4px rgba(0,0,0,0.05)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 4px 8px rgba(0,0,0,0.08)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 8px 16px rgba(0,0,0,0.1)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 16px 32px rgba(0,0,0,0.12)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 24px 48px rgba(0,0,0,0.15)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 32px 64px rgba(0,0,0,0.18)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 40px 80px rgba(0,0,0,0.2)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 48px 96px rgba(0,0,0,0.22)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 56px 112px rgba(0,0,0,0.25)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 64px 128px rgba(0,0,0,0.28)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 72px 144px rgba(0,0,0,0.3)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 80px 160px rgba(0,0,0,0.32)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 88px 176px rgba(0,0,0,0.35)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 96px 192px rgba(0,0,0,0.38)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 104px 208px rgba(0,0,0,0.4)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 112px 224px rgba(0,0,0,0.42)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 120px 240px rgba(0,0,0,0.45)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 128px 256px rgba(0,0,0,0.48)',
    '0 0 0 1px rgba(255,255,255,0.1), 0 136px 272px rgba(0,0,0,0.5)',
  ],
  typography: {
    fontFamily: '"Inter", "SF Pro Display", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      letterSpacing: '-0.025em',
      background: 'linear-gradient(135deg, #0a1525 0%, #14264a 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.015em',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
      background: 'linear-gradient(135deg, #0a1525 0%, #14264a 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    h5: {
      fontWeight: 500,
      letterSpacing: '-0.005em',
    },
    h6: {
      fontWeight: 500,
    },
    body1: {
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontWeight: 400,
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none', // Remove uppercase transform
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          // Fix for scrollbar shift when modals/dropdowns open
          scrollbarGutter: 'stable',
          overflowX: 'hidden',
          // Reserve space for scrollbar to prevent layout shift
          '--scrollbar-width': 'calc(100vw - 100%)',
        },
        body: {
          background: '#f8fafc', // Solid background
          minHeight: '100vh',
          // Fix for scrollbar shift when modals/dropdowns open
          scrollbarGutter: 'stable',
          width: '100%',
          overflowX: 'hidden',
          // Prevent body scroll lock from causing layout shift
          '&.MuiModal-open, &.MuiPopover-open, &.MuiMenu-open': {
            paddingRight: 'var(--scrollbar-width)',
            overflow: 'hidden',
          }
        },
        // Fix for Material-UI components causing scroll shift
        '.MuiModal-root, .MuiPopover-root, .MuiMenu-root': {
          scrollbarGutter: 'stable',
        },
        // Ensure all elements respect stable scrollbar
        '*': {
          scrollbarGutter: 'stable',
        }
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          background: '#ffffff',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          transition: 'box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.06)',
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 20px',
          transition: 'all 0.2s ease',
        },
        contained: {
          background: '#14264a',
          '&:hover': {
            background: '#0a1525',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(20, 38, 74, 0.3)',
          }
        },
        outlined: {
          border: '2px solid #14264a',
          color: '#14264a',
          '&:hover': {
            background: 'rgba(20, 38, 74, 0.05)',
            borderColor: '#0a1525',
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            background: '#ffffff',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#14264a',
              }
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#14264a',
                borderWidth: '2px',
              }
            }
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 500,
          backdropFilter: 'blur(10px)',
        }
      }
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          background: '#ffffff',
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            background: '#f8fafc',
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          background: '#ffffff',
          border: '1px solid #e2e8f0',
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: '#1c1c84',
          borderRight: '1px solid rgba(255,255,255,0.1)',
          color: '#ffffff', // Ensure text is white
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          margin: '4px 12px',
          color: '#ffffff', // White text for menu items
          '& .MuiListItemText-primary': {
            color: '#ffffff',
            fontWeight: 500,
          },
          '& .MuiListItemIcon-root': {
            color: 'rgba(255,255,255,0.8)',
          },
          '&:hover': {
            background: 'rgba(255,255,255,0.1)',
            '& .MuiListItemIcon-root': {
              color: '#ffffff',
            },
          },
          '&.Mui-selected': {
            background: 'rgba(255,255,255,0.15)',
            color: '#ffffff',
            '& .MuiListItemText-primary': {
              color: '#ffffff',
              fontWeight: 600,
            },
            '& .MuiListItemIcon-root': {
              color: '#ffffff',
            },
            '&:hover': {
              background: 'rgba(255,255,255,0.2)',
            }
          }
        }
      }
    },
    // Fix scrollbar shift for Select dropdowns
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiSelect-select': {
            // Prevent layout shift when dropdown opens
            scrollbarGutter: 'stable',
          }
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          // Prevent body scroll changes
          '& .MuiPaper-root': {
            scrollbarGutter: 'stable',
          }
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          // Prevent scrollbar shift when popover opens
          scrollbarGutter: 'stable',
        }
      }
    }
  }
});

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <NotificationProvider>
          <Router>
            <AuthProvider>
              <CompanyProvider>
                <AppRoutes />
                <TwoFactorModal />
                <PasswordChangeWrapper />
                <LegalAcceptanceWrapper />
              </CompanyProvider>
            </AuthProvider>
          </Router>
        </NotificationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicRoute>
            <AuthContainer />
          </PublicRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SupportPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/auth/callback" element={<OAuthCallback />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <AuthContainer />
          </PublicRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Dashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AnalyticsDashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gdpr-management"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <GDPRManagement />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pass-manager/membership-cards"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <MembershipCards />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Membership Members Management */}
      <Route
        path="/membership/members"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <MembershipManagement />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Tier management view */}
      <Route
        path="/membership/tiers"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <MembershipCards />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Coupon Offers Management */}
      <Route
        path="/coupons/offers"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CouponOffers />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Coupons Management */}
      <Route
        path="/coupons/coupons"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Coupons />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Legacy Coupon Management route - redirect to offers */}
      <Route
        path="/coupons"
        element={<Navigate to="/coupons/offers" replace />}
      />
      {/* Event Tickets - Venues */}
      <Route
        path="/event-tickets/venues"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Venues />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Event Tickets - Events */}
      <Route
        path="/event-tickets/events"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Events />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Event Tickets - Tickets */}
      <Route
        path="/event-tickets/tickets"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Tickets />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <UserManagement />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <DashboardLayout>
              <UserManagement />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/companies"
        element={
          <AdminRoute>
            <DashboardLayout>
              <CompanyManagement />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/locations"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Locations />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources"
        element={
          <AdminRoute>
            <DashboardLayout>
              <ResourceManagement />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/security"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SecuritySettings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AccountSettings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/api-keys"
        element={
          <AdminRoute>
            <DashboardLayout>
              <AdminAPIKeysManagement />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route        path="/admin/audit-logs"
        element={
          <AdminRoute>
            <DashboardLayout>
              <AuditLogs />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/performance"
        element={
          <AdminRoute>
            <DashboardLayout>
              <PerformanceMonitoringDashboard />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/companies"
        element={
          <AdminRoute>
            <DashboardLayout>
              <CompanyList />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/companies/create"
        element={
          <AdminRoute>
            <DashboardLayout>
              <CreateCompany />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/companies/:companyId/edit"
        element={
          <AdminRoute>
            <DashboardLayout>
              <EditCompany />
            </DashboardLayout>
          </AdminRoute>
        }
      />
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect if logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Legal Acceptance Wrapper Component
const LegalAcceptanceWrapper: React.FC = () => {
  const { isLegalModalOpen, acceptLegalTerms } = useAuth();

  const handleAccept = async () => {
    try {
      await acceptLegalTerms();
    } catch (error) {
      logger.error('[LegalAcceptanceWrapper] Failed to accept terms:', error);
    }
  };

  return (
    <LegalAcceptanceModal
      open={isLegalModalOpen}
      onAccept={handleAccept}
      canClose={false} // Cannot close until accepted
    />
  );
};

export default App;
